<template>
  <div class="marketplace-full-cart">
    <transition name="fade-up">
      <SloModal
        v-if="saveCartModal"
        :max-width="500"
        @formSubmit="saveCart"
        @dismiss="saveCartModal = false"
      >
        <template v-slot:title>
          {{ 'Save this cart?' | t }}
        </template>
        <p>
          {{ 'All of the items in your cart will be saved for later and can be accessed in your account.' | t }}
        </p>
        <div v-if="saveCartErrors" v-html="saveCartErrors" class="errors" />
        <SloField>
          <SloLabel>{{ 'Cart name' | t }}</SloLabel>
          <SloInput ref="saveCartNameInput" name="cartName" v-model="saveCartName" type="text" />
        </SloField>
        <template v-slot:footer>
          <SloButton type="submit" :disabled="saveCartLoading || !saveCartName" fill="outline" class="flex-1">
            <SloLoader :loading="saveCartLoading" size="md">
              {{ 'Save Cart' | t }}
            </SloLoader>
          </SloButton>
          <SloButton @click="saveCartModal = false" :disabled="saveCartLoading" class="flex-1">
            {{ 'Cancel' | t }}
          </SloButton>
        </template>
      </SloModal>
    </transition>
    <transition name="fade-up">
      <SloModal v-if="clearCartModal" @dismiss="clearCartModal = false" :form="false" :max-width="500">
        <template v-slot:title>
          {{ 'Clear cart?' | t }}
        </template>
        <p>{{ 'All items in your cart will be deleted.' | t }}</p>
        <template v-slot:footer>
          <SloButton @click="deleteCurrentCart" :disabled="cartLoading" fill="outline" class="flex-1">
            <SloLoader :loading="cartLoading" size="md">
              {{ 'Yes, clear cart' | t }}
            </SloLoader>
          </SloButton>
          <SloButton @click="clearCartModal = false" :disabled="cartLoading" class="flex-1">
            {{ 'No, keep my cart' | t }}
          </SloButton>
        </template>
      </SloModal>
    </transition>
    <h1 class="cart-page-title text-2xl fw-book m-0">{{ 'My Cart' | t }}</h1>
    <div>
      <div class="marketplace-full-cart--cart">
        <header class="flex items-center justify-between">
          <div class="flex items-center">
            <p class="text-xl">{{ cartTotalUnitCount }} {{ 'items in cart' | t }}</p>
          </div>
          <div class="marketplace-full-cart--button-wrapper flex items-center justify-start">
            <button
              v-if="authenticated"
              @click="saveCartModal = true"
              class="marketplace-cart--save-cart p-0 bg-transparent text-sm c-blue-400 hover:c-blue-700"
            >
              {{ 'Save cart for later' | t }}
            </button>
            <SloLink
              v-if="authenticated"
              :to="{name: 'marketplace-account-saved-carts'}"
              class="marketplace-cart--saved-carts text-sm"
            >
              {{ 'View saved carts' | t }}
            </SloLink>
            <button
              @click="clearCartModal = true"
              class="marketplace-cart--clear-cart flex items-center p-0 bg-transparent text-sm c-blue-400 hover:c-blue-700"
            >
              <SloIcon name="XMark" />
              <span class="block ml-xs">{{ 'Clear Cart' | t }}</span>
            </button>
          </div>
        </header>
        <div class="marketplace-full-cart--cart-products">
          <CartProduct
            v-for="cartEntry in cartEntries"
            :key="`cart-entry-${cartEntry?.entryNumber}`"
            :entry-number="cartEntry?.entryNumber"
            :quantity="cartEntry?.quantity"
            :estimated-ship-date="cartEntry?.product?.estimatedShipDate"
            :min-order-quantity="cartEntry?.product?.minOrderQuantity"
            :restricted-message="cartEntry?.product?.restrictedMessage"
            :price-each="cartEntry?.product?.price?.formattedValue"
            :price-each-subscription="cartEntry?.product?.subscriptionPrice?.formattedValue"
            :uom="cartEntry?.product?.uom"
            :total-price="cartEntry?.totalPrice?.formattedValue"
            :title="cartEntry?.product?.number"
            :description="cartEntry?.product?.title"
            :sku="cartEntry?.product?.code"
            :product-type="cartEntry?.product?.type"
            :is-returnable="cartEntry?.product?.isReturnable"
            :is-cancellable="cartEntry?.product?.isCancellable"
            :is-full-cart="true"
            :subscription-frequencies="cartEntry?.product?.subscriptionFrequencies"
            :subscription-frequency="cartEntry?.subscriptionFrequency"
            :subscription-percentage-discount="cartEntry?.product?.subscriptionPercentageDiscount"
            @removeProduct="handleRemoveProduct(cartEntry?.entryNumber)"
            @quantityChange="handleQuantityChange($event, cartEntry?.entryNumber, cartEntry?.quantity)"
            @frequencyChange="handleFrequencyChange($event, cartEntry?.entryNumber, cartEntry?.subscriptionFrequency)"
          >
            <template v-slot:image>
              <SloProductImage :source="cartEntry?.product?.images | getProductThumbnail" />
            </template>
          </CartProduct>
        </div>
      </div>
    </div>
    <div class="marketplace-full-cart--order-summary">
      <div>
        <header>
          <h2 class="text-xl fw-strong m-0">{{ 'Order Summary' | t }}</h2>
        </header>
        <div>
          <div class="marketplace-full-cart--order-summary--wrapper">
            <div class="marketplace-full-cart--order-summary--details">
              <div class="marketplace-full-cart--order-summary--details--item">
                <p class="m-0">{{ 'Subtotal' | t }}</p>
                <p class="m-0">{{ cartSubTotal }}</p>
              </div>
              <div class="marketplace-full-cart--order-summary--details--item">
                <form class="marketplace-promo-codes w-100" @submit.prevent="handleApplyCoupon" action="#">
                  <div
                    v-if="cartCouponCode"
                    class="flex flex-row items-center justify-between flex-wrap gap-md"
                  >
                    <div>
                      <p class="m-0">{{ 'Coupon' | t }} ({{ cartCouponCode }})</p>
                      <SloButton @click="handleRemoveCoupon" fill="clear" class="mt-sm p-0">
                        {{ 'Remove' | t }}
                      </SloButton>
                    </div>
                    <p class="m-0">
                      {{ cartDiscounts !== '$0.00' ? `-${cartDiscounts}` : '––' }}
                    </p>
                  </div>
                  <div v-else class="flex flex-row items-center justify-between flex-wrap gap-md">
                    <p class="m-0">{{ 'Coupon Code' | t }}</p>
                    <SloButton v-if="showPromoCodeInput" fill="clear" class="p-0" @click="showPromoCodeInput = false">
                      {{ 'Cancel' | t }}
                    </SloButton>
                    <SloButton v-else fill="clear" class="p-0" @click="showPromoCodeInput = true">
                      {{ 'Add' | t }}
                    </SloButton>
                  </div>
                  <div v-if="showPromoCodeInput" class="flex items-center justify-between gap-md mt-md">
                    <SloInput
                      v-model="couponCode"
                      type="text"
                      name="coupon_code"
                      :placeholder="'Enter your coupon code' | t"
                      class="flex-1 m-0"
                    />
                    <SloButton type="submit" fill="outline">
                      {{ 'Apply' | t }}
                    </SloButton>
                  </div>
                </form>
              </div>
              <div class="marketplace-full-cart--order-summary--details--item">
                <p class="m-0">{{ 'Shipping' | t }}</p>
                <p class="m-0">––</p>
              </div>
              <div class="marketplace-full-cart--order-summary--details--item">
                <p class="m-0">{{ 'Taxes' | t }}</p>
                <p class="m-0">––</p>
              </div>
            </div>
            <div class="marketplace-full-cart--order-summary--total">
              <p class="m-0 fw-bold">{{ 'Total' | t }}</p>
              <p class="m-0 text-xl">{{ cartTotal }}</p>
            </div>
          </div>
        </div>
      </div>
      <div class="marketplace-full-cart--order-summary--button-wrapper">
        <SloButton
          @click="handleContinueClick"
          fill="outline"
          class="marketplace-cart--continue"
        >
          {{ 'Continue Shopping' | t }}
        </SloButton>
        <SloButton
          @click="handleCheckoutClick"
          class="marketplace-cart--checkout"
        >
          {{ 'Secure Checkout' | t }}
        </SloButton>
      </div>
    </div>
    <div class="marketplace-badge--user">
      <figure class="marketplace-badge">
        <SloanMarketPlaceBadge />
      </figure>
    </div>
    <ReferencesFullCarousel
      v-if="cartReferences && cartReferences.length > 0"
      :loading="cartLoading"
      :references="cartReferences"
    />
  </div>
</template>

<script>
import {mapState, mapGetters, mapActions} from 'vuex';
import t from '../../main/i18n';
import {marketplaceApi} from '../apis/marketplace';
import {getProductThumbnail, tagManagerTrack} from '../../main/utils';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloProductImage from '../../../lib/components/SloProductImage.vue';
import SloIcon from '../../../lib/components/SloIcon.vue';
import SloLink from '../../../lib/components/SloLink.vue';
import SloAddToCart from '../../../lib/components/SloAddToCart.vue';
import SloCard from '../../../lib/components/SloCard.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import CartProduct from '../components/CartProduct.vue';
import ReferencesFullCarousel from '../components/ReferencesFullCarousel.vue';
import SloanMarketPlaceBadge from '../../../img/Sloan-Marketplace-badge.svg';

export default {
  components: {
    SloField,
    SloLabel,
    SloInput,
    SloLoader,
    SloButton,
    SloProductImage,
    SloIcon,
    SloLink,
    SloAddToCart,
    SloCard,
    SloModal,
    CartProduct,
    ReferencesFullCarousel,
    SloanMarketPlaceBadge
  },
  filters: {
    t,
    getProductThumbnail
  },
  data() {
    return {
      saveCartLoading: false,
      saveCartModal: false,
      saveCartName: null,
      saveCartErrors: false,
      clearCartModal: false,
      showPromoCodeInput: false,
      couponCode: ''
    };
  },
  watch: {
    saveCartModal(newValue) {
      if (newValue) {
        this.$nextTick(() => this.$refs.saveCartNameInput.setFocus());
      }
    },
    showPromoCodeInput(newValue) {
      if (!newValue) this.couponCode = '';
    }
  },
  computed: {
    ...mapState('marketplace', ['cartLoading', 'cartResponse', 'cartReferences']),
    ...mapGetters('auth', ['authenticated', 'user']),
    ...mapGetters('marketplace', [
      'cartId',
      'cartEntries',
      'cartTotalUnitCount',
      'cartTotal',
      'cartSubTotal',
      'cartDiscounts',
      'cartCouponCode'
    ])
  },
  methods: {
    ...mapActions('marketplace', [
      'removeCartEntry', 'updateCartEntryQuantity', 'deleteCurrentCart'
    ]),
    async handleRemoveProduct(entryNumber) {
      await this.removeCartEntry({entryNumber});
    },
    async handleQuantityChange(quantity, entryNumber, prevQuantity) {
      if (prevQuantity === quantity) return;
      const cartEntry = this.cartEntries.find(entry => entry.entryNumber === entryNumber);
      const frequency = cartEntry?.subscriptionFrequency;
      await this.updateCartEntryQuantity({entryNumber, quantity, frequency});
    },
    async handleFrequencyChange(frequency, entryNumber, prevFrequency) {
      if (prevFrequency === frequency) return;
      const cartEntry = this.cartEntries.find(entry => entry.entryNumber === entryNumber);
      const quantity = cartEntry?.quantity;
      await this.updateCartEntryQuantity({entryNumber, quantity, frequency});
    },
    async handleApplyCoupon() {
      await marketplaceApi.applyCoupon(this.couponCode)
        .then(async() => {
          this.couponCode = '';
          await this.$store.dispatch('marketplace/getCurrentCart');
          this.$snotify.success(t('Coupon applied'));
        })
        .catch(e => {
          if (e?.response?.data?.errors) {
            const errors = e?.response?.data?.errors;
            this.$snotify.error(t(errors[0]?.message));
          } else {
            this.$snotify.error(t('Unable to apply selected coupon'));
          }
        });
    },
    async handleRemoveCoupon() {
      this.couponCode = '';

      await marketplaceApi.removeCoupon(this.cartCouponCode)
        .then(async() => {
          await this.$store.dispatch('marketplace/getCurrentCart');
          this.$snotify.success(t('Coupon removed'));
        })
        .catch(() => {
          this.$snotify.error(t('Unable to remove coupon'));
        });
    },
    async handleCheckoutClick() {
      tagManagerTrack('begin_checkout', {
        currency: this.cartResponse?.subTotal?.currencyIso,
        user: this.user?.accountNumber || 'guest',
        cartId: this.cartId,
        cartSubTotal: this.cartResponse?.subTotal?.value,
        items: this.cartEntries.map(cartEntry => ({
          item_id: cartEntry?.product?.code,
          item_name: cartEntry?.product?.number,
          item_category: cartEntry?.product?.type,
          price: cartEntry?.product?.price?.value,
          quantity: cartEntry?.quantity,
          totalPrice: cartEntry?.totalPrice?.value
        }))
      });

      await this.$router.push({name: 'marketplace-checkout'});
    },
    handleContinueClick() {
      window.location = document.referrer;
    },
    async saveCart() {
      this.saveCartLoading = true;
      await marketplaceApi.saveCurrentCart(this.saveCartName)
        .then(async() => {
          this.$store.commit('marketplace/reset_cart_data');
          this.saveCartLoading = false;
          this.saveCartModal = false;
          this.saveCartErrors = false;
          await this.$router.push({name: 'marketplace-account-saved-carts'});
          this.$snotify.success(t('Cart saved'));
          await this.$store.dispatch('marketplace/getCurrentCart');
        })
        .catch(e => {
          this.saveCartLoading = false;
          if (e?.response?.data?.errors[0]?.message) {
            this.saveCartErrors = e?.response?.data?.errors[0]?.message;
          } else {
            this.saveCartErrors = [];
            this.$snotify.error(t('Unable to save cart'));
          }
        });
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../../../scss/variables';
@import '../../../scss/system';

.marketplace-full-cart {
  display: flex;
  flex-direction: column;
  gap: 3.2rem;
  overflow-x: hidden;
  margin-block-end: 5.6rem;
  margin-inline: -1.5rem;
  padding-inline: 1.5rem;

  @media (min-width: $screen-md) {
    display: grid;
    grid-template-columns: 2fr 1fr;
  }

  .cart-page-title {
    grid-column: 1 / -1;
  }

  header {
    flex-wrap: wrap;
    gap: 1.2rem;
  }

  &--button-wrapper {
    flex-wrap: wrap;
    gap: 1.2rem;

    button {
      font-weight: 500;
    }

    a {
      color: $color-blue-400;
      font-weight: 500;

      &:hover {
        color: $color-blue-700;
      }

      &:after {
        content: none;
      }
    }
  }

  &--cart, &--order-summary > div:first-of-type {
    border: 1px solid $color-gray-400;
    border-radius: 1.2rem;

    header {
      padding: 1.6rem;
      border-bottom: 1px solid $color-gray-400;

      @media (min-width: $screen-md) {
        padding: 2.4rem;
      }
    }
  }

  &--order-summary {
    &--wrapper {
      padding-inline: 2.4rem;
    }

    &--details {
      display: flex;
      flex-direction: column;
      gap: 0.8rem;
      padding-block: 2.4rem;
      border-bottom: 1px solid #d1d5db;

      &--item {
        display: flex;
        justify-content: space-between;
      }
    }

    &--total {
      display: flex;
      justify-content: space-between;
      margin-block-start: 1.6rem;
      padding-block: 1.6rem;
    }

    &--button-wrapper {
      display: flex;
      flex-direction: column;
      align-items: stretch;
      justify-content: start;
      gap: 1.6rem;
      flex-wrap: wrap;
      margin-block-start: 3.2rem;

      > * {
        flex: 1 1 48%;
      }
    }
  }

  .fade-up-enter, .fade-up-leave-to {
    opacity: 0;
    transform: translateY(20px);
  }

  .fade-up-enter-active, .fade-up-leave-active {
    transition: opacity 0.3s, transform 0.3s;
  }

  :deep(.slo-modal-frame) {
    header button {
      padding: 0 !important;
    }
  }

  .errors {
    width: 100%;
    padding: 16px;
    background: rgba($color-red-400, 0.2);
    color: $color-red-400;
    font-weight: 500;
    border-radius: 4px;
  }

  footer {
    grid-column: 1 / 3;
  }

  // This overwrites the styles for the cart product in mini cart
  :deep(.slo-cart-product:not(.up-sell)) {

    &:only-child, &:last-child {
      border: none;
    }

    .slo-cart-product--wrapper {
      grid-template-columns: -moz-fit-content(90px) 1fr 1fr 1fr;
      grid-template-columns: fit-content(90px) 1fr 1fr 1fr;
      padding-inline: 1.6rem;

      @media (min-width: $screen-md) {
        padding-inline: 2.4rem;
      }
    }

    .slo-cart-product--price {
      grid-column-start: 1;
      justify-content: end;
      width: 100%;

      @media (min-width: $screen-sm) {
        grid-column-start: 2;
      }

      @media (min-width: $screen-md) {
        grid-column: 2 / 5;
        justify-content: space-between;
      }

      .slo-price-info {
        flex-grow: 0;
        flex-shrink: 1;
        flex-basis: 100%;

        @media (min-width: 1292px) {
          flex-basis: 200px;
        }

        > div {
          flex-wrap: wrap;
        }

        span {
          @media (min-width: $screen-md) {
            text-align: right;
          }
        }
      }

      label {
        flex: 1 1 50%;
        margin: 0;

        @media (min-width: $screen-sm) {
          flex: 0 0 50%;
        }

        @media (min-width: 1292px) {
          flex: 0 0 30%;
        }
      }

      span {
        margin-left: auto;

        @media (min-width: $screen-md) {
          margin-left: 0;
        }
      }
    }

    .slo-product-image img {
      width: 100%;
      height: 100%;
    }
  }

  :deep(.slo-loader-icon) {
    min-height: 195px;
  }
}

.marketplace-badge--wrapper {
  @media (min-width: $screen-md) {
    grid-column: 1 / -1;
  }
}

.marketplace-badge {
  width: 100%;
  max-width: 312px;
  padding-block: 2.4rem;

  svg {
    width: 100%;
    height: 100%;
  }
}

.marketplace-promo-codes {
  :deep(label) {
    margin: 0;

    input {
      height: 46px !important;
    }
  }
}
</style>
